import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import { Date, LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { AgeCategory } from '@mews-commander/data-access';
import { NotesCell } from '../../../components/cells/NotesCell';
import { GuestsCell } from '../../../components/cells/GuestsCell';
import { ProductsCell } from '../../../components/cells/ProductsCell';
import { ResourceCell } from '../../../components/cells/ResourceCell';
import { TotalCell } from '../../../components/cells/TotalCell';
import { ActionCell } from '../../../components/cells/ActionCell';
import { OwnerCell } from '../../../components/cells/OwnerCell';
import { ProductCheckListTableColumn, ProductCheckListTableRow } from '../types';
import { getAgeCategoryIdentifier, getAgeCategoryLabel, getAgeCategoryTotalCellId } from '../../../utils/ageCategories';
import { isRowActive } from './row';

type DailyProductColumnDef<T extends keyof ProductCheckListTableRow> = ColumnDef<
    ProductCheckListTableRow,
    ProductCheckListTableRow[T]
>;

const columnHelper = createColumnHelper<ProductCheckListTableRow>();

const resourceColumn: DailyProductColumnDef<'resourceId'> = columnHelper.accessor(
    ProductCheckListTableColumn.Resource,
    {
        id: ProductCheckListTableColumn.Resource,
        enableSorting: true,
        header: () => <LocalizedText textKey={TextKey.Space} />,
        cell: (props) => <ResourceCell row={props.row.original} active={isRowActive(props.row.original)} />,
    }
);

const notesColumn: DailyProductColumnDef<'notes'> = columnHelper.accessor(ProductCheckListTableColumn.Notes, {
    id: ProductCheckListTableColumn.Notes,
    header: () => <LocalizedText textKey={TextKey.Notes} />,
    cell: (props) =>
        !isRowActive(props.row.original) ? null : (
            <NotesCell count={props.getValue().length} reservationId={props.row.original.reservation?.id} />
        ),
});

const getGuestsColumn = (reportFilterDate: Date): DailyProductColumnDef<'guests'> =>
    columnHelper.accessor(ProductCheckListTableColumn.Guests, {
        id: ProductCheckListTableColumn.Guests,
        header: () => <LocalizedText textKey={TextKey.GuestPlural} />,
        cell: (props) => (
            <GuestsCell
                row={props.row.original}
                reportFilterDate={reportFilterDate}
                active={isRowActive(props.row.original)}
            />
        ),
    });

const ownerColumn: DailyProductColumnDef<'owner'> = columnHelper.accessor(ProductCheckListTableColumn.Owner, {
    id: ProductCheckListTableColumn.Owner,
    enableSorting: true,
    header: () => <LocalizedText textKey={TextKey.Owner} />,
    cell: (props) => <OwnerCell row={props.row.original} active={isRowActive(props.row.original)} />,
});

const productsColumn: DailyProductColumnDef<'productsData'> = columnHelper.accessor(
    ProductCheckListTableColumn.Products,
    {
        id: ProductCheckListTableColumn.Products,
        header: () => <LocalizedText textKey={TextKey.Products} />,
        cell: (props) => <ProductsCell row={props.row.original} active={isRowActive(props.row.original)} />,
    }
);

const totalColumnMeta = {
    align: 'end',
    width: 80,
} as const;

const totalCountColumn: DailyProductColumnDef<'productsData'> = columnHelper.accessor(
    ProductCheckListTableColumn.Products,
    {
        id: ProductCheckListTableColumn.Total,
        header: () => <LocalizedText textKey={TextKey.TotalAmountQuantity} />,
        cell: (props) => <TotalCell row={props.row.original} active={isRowActive(props.row.original)} />,
        meta: totalColumnMeta,
    }
);

const actionColumn = columnHelper.accessor(ProductCheckListTableColumn.Id, {
    id: ProductCheckListTableColumn.Action,
    header: () => null,
    cell: (props) => <ActionCell row={props.row.original} />,
    meta: {
        width: 120,
    },
});

const getAgeCategoriesColumns = (ageCategories: AgeCategory[]) => {
    const result = ageCategories.map((ageCategory) => {
        const ageCategoryIdentifier = getAgeCategoryIdentifier(ageCategory.id);
        return columnHelper.accessor('productsData', {
            id: getAgeCategoryTotalCellId(ageCategoryIdentifier),
            meta: totalColumnMeta,
            header: () => getAgeCategoryLabel(ageCategory),
            cell: (props) => (
                <TotalCell
                    row={props.row.original}
                    active={isRowActive(props.row.original)}
                    ageCategoryIdentifier={ageCategoryIdentifier}
                />
            ),
        });
    });

    const noneIdentifier = getAgeCategoryIdentifier(null);
    result.push(
        columnHelper.accessor('productsData', {
            id: getAgeCategoryTotalCellId(noneIdentifier),
            meta: totalColumnMeta,
            header: () => getAgeCategoryLabel(null),
            cell: (props) => (
                <TotalCell
                    row={props.row.original}
                    active={isRowActive(props.row.original)}
                    ageCategoryIdentifier={noneIdentifier}
                />
            ),
        })
    );

    return result;
};

export const useTableColumns = (reportFilterDate: Date, ageCategories: AgeCategory[]) =>
    useMemo(
        () => [
            resourceColumn,
            notesColumn,
            getGuestsColumn(reportFilterDate),
            ownerColumn,
            productsColumn,
            totalCountColumn,
            ...getAgeCategoriesColumns(ageCategories),
            actionColumn,
        ],
        [reportFilterDate, ageCategories]
    );
